import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button, CenteredCard, Input, Logo, Notice } from '../components/ui';
import { colors, type, radius, transition } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

export default function Login() {
  const { signInWithPassword, signUp, signInWithStudio } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'studio' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await signInWithPassword(email, password);
    setLoading(false);
    if (error) setError(error.message);
  }

  async function handleSignup(e) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    const { error } = await signUp(email, password, displayName);
    setLoading(false);
    if (error) setError(error.message);
  }

  async function handleStudio(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await signInWithStudio(email, password);
    setLoading(false);
    if (error) setError(error.message);
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setResetSent(true);
    } catch {
      setError('Failed to send reset email');
    }
    setLoading(false);
  }

  function switchMode(newMode) {
    setMode(newMode);
    setError(null);
    setEmail('');
    setPassword('');
    setDisplayName('');
    setResetSent(false);
  }

  const field = { height: '36px', fontSize: type.base };

  return (
    <CenteredCard width="400px" style={{ padding: '36px 32px 28px' }}>
      <div style={s.logoSection}>
        {/* Stacked lockup per the kit: login screens and square spaces. */}
        <Logo variant="stacked" size={72} as="h1" />
        <p style={s.tagline}>Internal file storage for Mayday Media</p>
      </div>

      {/* ─── Tab switcher ─── */}
      {mode !== 'studio' && (
        <div style={s.tabs} role="tablist">
          <button
            role="tab"
            aria-selected={mode !== 'signup'}
            onClick={() => switchMode('login')}
            style={{ ...s.tab, ...(mode !== 'signup' ? s.tabActive : {}) }}
          >
            Sign in
          </button>
          <button
            role="tab"
            aria-selected={mode === 'signup'}
            onClick={() => switchMode('signup')}
            style={{ ...s.tab, ...(mode === 'signup' ? s.tabActive : {}) }}
          >
            Create account
          </button>
        </div>
      )}

      {/* ─── Cloud Login ─── */}
      {mode === 'login' && (
        <form onSubmit={handleLogin} style={s.form}>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" required style={field} autoFocus />
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required style={field} />
          <Button type="submit" variant="primary" size="lg" block disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
          {error && <Notice tone="danger">{error}</Notice>}
          <button type="button" className="mc-link" onClick={() => switchMode('forgot')} style={s.link}>
            Forgot password?
          </button>
        </form>
      )}

      {/* ─── Forgot Password ─── */}
      {mode === 'forgot' && (
        <form onSubmit={handleForgot} style={s.form}>
          {resetSent ? (
            <>
              <Notice tone="success">If an account exists for that email, a reset link has been sent.</Notice>
              <Button type="button" variant="primary" size="lg" block onClick={() => switchMode('login')}>
                Back to sign in
              </Button>
            </>
          ) : (
            <>
              <div style={s.help}>Enter your email and we'll send you a reset link.</div>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" required style={field} autoFocus />
              <Button type="submit" variant="primary" size="lg" block disabled={loading}>
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
              {error && <Notice tone="danger">{error}</Notice>}
              <button type="button" className="mc-link" onClick={() => switchMode('login')} style={s.link}>
                Back to sign in
              </button>
            </>
          )}
        </form>
      )}

      {/* ─── Cloud Sign Up ─── */}
      {mode === 'signup' && (
        <form onSubmit={handleSignup} style={s.form}>
          <Input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Display name (optional)" style={field} autoFocus />
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" required style={field} />
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password (min 8 characters)" required style={field} />
          <Button type="submit" variant="primary" size="lg" block disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </Button>
          {error && <Notice tone="danger">{error}</Notice>}
        </form>
      )}

      {/* ─── Studio Login ─── */}
      {mode !== 'studio' ? (
        <>
          <div style={s.divider}>
            <span style={s.dividerLine} />
            <span style={s.dividerText}>or</span>
            <span style={s.dividerLine} />
          </div>
          <Button type="button" size="lg" block onClick={() => switchMode('studio')} style={s.studioButton}
            icon={<StudioGlyph />}>
            Sign in with Mayday Studio
          </Button>
        </>
      ) : (
        <form onSubmit={handleStudio} style={s.form}>
          <div style={s.studioLabel}>
            <StudioGlyph />
            <span>Mayday Studio credentials</span>
          </div>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Studio email" required style={field} autoFocus />
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Studio password" required style={field} />
          <Button type="submit" variant="primary" size="lg" block disabled={loading}>
            {loading ? 'Connecting…' : 'Sign in with Studio'}
          </Button>
          <button type="button" className="mc-link" onClick={() => switchMode('login')} style={s.link}>
            Back to Cloud sign in
          </button>
          {error && <Notice tone="danger">{error}</Notice>}
        </form>
      )}
    </CenteredCard>
  );
}

// The Studio family mark: four rounded modules with the red live dot.
function StudioGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="1" y="1" width="6" height="6" rx="1.6" fill={colors.text} />
      <rect x="9" y="1" width="6" height="6" rx="1.6" fill={colors.accent} />
      <rect x="1" y="9" width="6" height="6" rx="1.6" fill={colors.sand} />
      <circle cx="12" cy="12" r="3" fill={colors.sync} />
    </svg>
  );
}

const s = {
  logoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '28px',
  },
  tagline: {
    fontSize: type.md,
    color: colors.textMuted,
    marginTop: '14px',
  },
  tabs: {
    display: 'flex',
    gap: '2px',
    marginBottom: '18px',
    background: colors.inputBg,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.lg,
    padding: '3px',
  },
  tab: {
    flex: 1,
    height: '28px',
    border: 'none',
    borderRadius: radius.md,
    color: colors.textMuted,
    fontSize: type.md,
    fontWeight: 500,
    cursor: 'pointer',
    transition,
  },
  tabActive: {
    background: colors.raised,
    color: colors.text,
    boxShadow: '0 1px 2px rgba(0,0,0,0.3), 0 0 0 1px rgba(239,230,221,0.06)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  help: {
    fontSize: type.md,
    color: colors.textMuted,
    textAlign: 'center',
    lineHeight: 1.4,
  },
  link: {
    color: colors.textMuted,
    fontSize: type.md,
    textAlign: 'center',
    padding: '6px 4px 0',
    transition,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '18px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: colors.border,
  },
  dividerText: {
    fontSize: type.xs,
    color: colors.textFaint,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  studioButton: {
    borderColor: colors.borderStrong,
    color: colors.text,
    fontWeight: 500,
  },
  studioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: type.md,
    color: colors.textSecondary,
    fontWeight: 500,
    marginBottom: '2px',
  },
};
